import Bugsnag, { type Client } from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import React from 'react';

let bugsnagClient: Client | null = null;

export function initBugsnag() {
  // Only initialize on client-side
  if (typeof window === 'undefined') {
    return null;
  }

  if (bugsnagClient) {
    return bugsnagClient;
  }

  const apiKey = process.env.NEXT_PUBLIC_BUGSNAG_API_KEY;

  if (!apiKey) {
    console.warn('[Bugsnag] API key not found (NEXT_PUBLIC_BUGSNAG_API_KEY). Error tracking is disabled.');
    return null;
  }

  try {
    console.log('[Bugsnag] Initializing client-side error tracking...');
    
    bugsnagClient = Bugsnag.start({
      apiKey,
      plugins: [new BugsnagPluginReact()],
      releaseStage: process.env.NODE_ENV || 'development',
      appVersion: process.env.NEXT_PUBLIC_APP_VERSION,
      enabledReleaseStages: ['production', 'staging', 'development'],
      enabledBreadcrumbTypes: ['error', 'log', 'navigation', 'request', 'state', 'user'],
      maxBreadcrumbs: 50,
      // User context is set by BugsnagProvider via client.setUser()
    });
    
    console.log('[Bugsnag] Client-side error tracking initialized successfully');

    // Add global error handlers for unhandled exceptions
    window.addEventListener('error', (event) => {
      if (bugsnagClient) {
        bugsnagClient.notify(event.error || new Error(event.message), (bugsnagEvent) => {
          bugsnagEvent.addMetadata('errorEvent', {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
          });
        });
      }
    });

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      if (bugsnagClient) {
        const error = event.reason instanceof Error 
          ? event.reason 
          : new Error(String(event.reason));
        bugsnagClient.notify(error, (bugsnagEvent) => {
          bugsnagEvent.addMetadata('promise', {
            type: 'unhandledrejection',
            reason: String(event.reason),
          });
        });
      }
    });

    return bugsnagClient;
  } catch (error) {
    console.error('Failed to initialize Bugsnag:', error);
    return null;
  }
}

// Initialize immediately on module load (client-side only)
if (typeof window !== 'undefined') {
  initBugsnag();
}

export function getBugsnagClient() {
  return bugsnagClient;
}

export function getBugsnagErrorBoundary() {
  const client = getBugsnagClient();
  if (!client) {
    // Return a pass-through component if Bugsnag is not initialized
    const PassThrough = ({ children }: { children: React.ReactNode }) => React.createElement(React.Fragment, null, children);
    return PassThrough;
  }
  
  const plugin = client.getPlugin('react');
  if (plugin) {
    return plugin.createErrorBoundary(React);
  }
  
  const PassThrough = ({ children }: { children: React.ReactNode }) => React.createElement(React.Fragment, null, children);
  return PassThrough;
}

export { Bugsnag };
